import {
  collection,
  getCountFromServer,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  startAfter,
  where
} from "firebase/firestore";
import { useEffect, useRef, useState } from "react";
import { setComplaints, setComplaintsCount } from "../Redux/complaints/action";
import { setErrorStatus } from "../Redux/status/action";
import { database } from "../Services/firebase";
import { getComplaint } from "../Services/database";
import { isValidObject } from "../Services/validators";

export function useComplaints(props) {
  const [complaintsListener, setComplaintsListener] = useState({
    listener: null
  });
  const [complaintsLoading, setComplaintsLoading] = useState({
    pagination: false,
    search: false,
    assets: false
  });
  const [searchData, setSearchData] = useState(null);
  const complaintsData = useRef(null);
  const currentCursor = useRef(null);
  const filterQuery = useRef([]);
  const listeningFilterData = useRef([]);
  const dataLimit = 50;

  const generateFirestoreQueryRef = (nextPage) => {
    let queryRef = query(
      collection(database, "complaints"),
      orderBy(props.orderBy || "createdAt", props.sortBy || "desc")
    );

    if (nextPage === true) {
      // Can't use startAfter() before calling orderBy() thats why it's placed here
      queryRef = query(queryRef, startAfter(currentCursor?.current));
    }

    filterQuery.current?.forEach((value, index) => {
      if ((index + 1) % 3 === 0 && index !== 0) {
        queryRef = query(
          queryRef,
          where(
            filterQuery.current[index - 2],
            filterQuery.current[index - 1],
            value
          )
        );
      }
    });

    return queryRef;
  };

  const getComplaintsNextPage = async (preLoadedLimit, latestComplaints) => {
    setComplaintsLoading((prev) => ({ ...prev, pagination: true }));
    if (!currentCursor?.current) {
      return setComplaintsLoading((prev) => ({ ...prev, pagination: false }));
    }

    let paginationQueryRef = generateFirestoreQueryRef(true, preLoadedLimit);

    paginationQueryRef = query(
      paginationQueryRef,
      limit(preLoadedLimit !== undefined ? preLoadedLimit : dataLimit)
    );

    const querySnapshot = await getDocs(paginationQueryRef);
    let complaints = {};
    querySnapshot.forEach((doc) => {
      complaints[doc.id] = {
        ...doc.data(),
        documentId: doc.id
      };
    });

    if (latestComplaints) {
      complaintsData.current = { ...latestComplaints, ...complaints };
    } else {
      complaintsData.current = { ...complaintsData.current, ...complaints };
    }

    currentCursor.current = querySnapshot.docs[querySnapshot.docs.length - 1];
    setComplaintsLoading((prev) => ({ ...prev, pagination: false }));

    setComplaints(complaintsData.current);
  };
  // need to verify about search only opened complaints for employee
  const getSearchedComplaint = async (complaintId) => {
    setComplaintsLoading((prev) => ({ ...prev, search: true }));
    const existData = complaintsData.current?.[complaintId.toUpperCase()];
    if (isValidObject(existData)) {
      setSearchData(existData);
    } else {
      const searchComplaint = await getComplaint(complaintId);
      if (isValidObject(searchComplaint)) {
        setSearchData(searchComplaint);
      } else {
        setSearchData("empty");
      }
    }
    setComplaintsLoading((prev) => ({ ...prev, search: false }));
  };

  const getComplaintsCount = async (queryRef) => {
    let complaintTotalCount = await getCountFromServer(queryRef);
    complaintTotalCount = complaintTotalCount.data().count;

    setComplaintsCount(complaintTotalCount);
  };

  const subscribeToComplaints = (phoneNumber) => {
    if (phoneNumber) {
      let queryRef = generateFirestoreQueryRef();

      const queryRefWithDataLimit = query(queryRef, limit(dataLimit));

      return onSnapshot(
        queryRefWithDataLimit,
        (dataSnapshot) => {
          let complaints = {};
          dataSnapshot.forEach((doc) => {
            complaints[doc.id] = {
              ...doc.data(),
              documentId: doc.id
            };
          });

          currentCursor.current =
            dataSnapshot.docs[dataSnapshot.docs.length - 1];

          if (
            complaintsData.current &&
            Object.values(complaintsData.current)?.length > dataLimit
          ) {
            const previousComplaintsTotal =
              Object.values(complaintsData.current)?.length - dataLimit;
            getComplaintsNextPage(previousComplaintsTotal, complaints);
          } else if (searchData) {
            if (isValidObject(searchData)) {
              complaintsData.current = null;
              complaintsData.current = {
                [props.searchKey]: {
                  ...searchData,
                  documentId: props.searchKey
                }
              };
            } else {
              complaintsData.current = null;
              complaintsData.current = {};
            }
          } else {
            complaintsData.current = complaints;
          }

          getComplaintsCount(queryRef);

          setComplaints(complaintsData.current);
        },
        (error) => {
          console.error(error, "from complaints");
          setErrorStatus(error);
        }
      );
    }
  };

  useEffect(() => {
    if (props.searchKey?.length === 8) {
      getSearchedComplaint(props.searchKey);
    } else if (props.searchKey?.length < 8 && searchData !== null) {
      setSearchData(null);
    }
    // eslint-disable-next-line
  }, [props.searchKey]);

  useEffect(() => {
    if (props.isAuth && typeof complaintsListener.listener === "function") {
      complaintsListener.listener();
      setComplaintsListener({
        listener: subscribeToComplaints(props.phoneNumber)
      });
    }
    // eslint-disable-next-line
  }, [props.sortBy, searchData]);

  useEffect(() => {
    if (props.isAuth && typeof complaintsListener.listener === "function") {
      complaintsListener.listener();
      complaintsData.current = null;
      setComplaintsListener({
        listener: subscribeToComplaints(props.phoneNumber)
      });
    }
    // eslint-disable-next-line
  }, [props.claims]);

  useEffect(() => {
    filterQuery.current = props.filterConverter(props.filterData);

    if (
      typeof complaintsListener.listener === "function" &&
      props.isAuth &&
      JSON.stringify(listeningFilterData.current) !==
        JSON.stringify(filterQuery.current)
    ) {
      complaintsData.current = null;
      listeningFilterData.current = filterQuery.current;
      complaintsListener.listener();
      setComplaintsListener({
        listener: subscribeToComplaints(props.phoneNumber)
      });
    }
    // eslint-disable-next-line
  }, [props.filterData, props.orderBy]);

  useEffect(() => {
    if (props.isAuth && complaintsListener.listener === null) {
      setComplaintsListener({
        listener: subscribeToComplaints(props.phoneNumber)
      });
    } else if (
      !props.isAuth &&
      typeof complaintsListener.listener === "function"
    ) {
      complaintsListener.listener();
      setComplaintsListener({
        listener: null
      });
    }
    // eslint-disable-next-line
  }, [props.isAuth, props.phoneNumber]);
  return [getComplaintsNextPage, complaintsLoading];
}
